/**
 * IDC (Indice de Dépense de Chaleur) Calculations
 * Canton de Genève - Heat Consumption Index utilities
 *
 * References:
 * - ge.ch official IDC thresholds and regulations
 * - Progressive threshold reductions (2022-2031)
 */

export interface IDCStatus {
  status: 'compliant' | 'optimization' | 'warning' | 'critical'
  color: string
  label: string
  message: string
  baseThreshold: number // Always 450 MJ/m²
  significantThreshold: number // Varies by year
  exceedancePercent: number
  requiresAction: boolean
  actionType: 'none' | 'optimization' | 'audit' | 'mandatory_renovation'
}

/**
 * Base regulatory threshold - always 450 MJ/m²·year (125 kWh/m²·year)
 */
export const IDC_BASE_THRESHOLD = 450 // MJ/m²·year

/**
 * Optimization threshold - 550 MJ/m²·year (153 kWh/m²·year)
 */
export const IDC_OPTIMIZATION_THRESHOLD = 550 // MJ/m²·year

/**
 * Get the "significant exceedance" threshold for a given year
 * This threshold decreases progressively according to Geneva regulations
 *
 * @param year - The year to check (default: current year)
 * @returns Significant threshold in MJ/m²·year
 */
export function getSignificantThreshold(
  year: number = new Date().getFullYear()
): number {
  if (year <= 2026) return 800 // MJ/m²
  if (year <= 2030) return 650 // MJ/m²
  return 550 // MJ/m² (from 2031 onwards)
}

/**
 * Convert MJ/m²·year to kWh/m²·year
 * 1 kWh = 3.6 MJ
 */
export function convertMJtoKWh(mj: number): number {
  return mj / 3.6
}

/**
 * Convert kWh/m²·year to MJ/m²·year
 * 1 kWh = 3.6 MJ
 */
export function convertKWhtoMJ(kwh: number): number {
  return kwh * 3.6
}

/**
 * Calculate percentage over the base threshold
 */
export function calculateExceedance(idc: number): number {
  return ((idc - IDC_BASE_THRESHOLD) / IDC_BASE_THRESHOLD) * 100
}

/**
 * Get comprehensive IDC status assessment
 *
 * @param idc - Heat consumption index in MJ/m²·year
 * @param currentYear - Year for threshold calculation (default: current year)
 * @returns Complete IDC status object
 */
export function getIDCStatus(
  idc: number,
  currentYear: number = new Date().getFullYear()
): IDCStatus {
  const significantThreshold = getSignificantThreshold(currentYear)
  const exceedancePercent = calculateExceedance(idc)

  // Critical: Above significant threshold - mandatory renovation required
  if (idc > significantThreshold) {
    return {
      status: 'critical',
      color: '#ef4444', // Red
      label: 'Rénovation obligatoire',
      message: `IDC dépasse le seuil de dépassement significatif (${significantThreshold} MJ/m²)`,
      baseThreshold: IDC_BASE_THRESHOLD,
      significantThreshold,
      exceedancePercent,
      requiresAction: true,
      actionType: 'mandatory_renovation',
    }
  }

  // Warning: Between optimization and significant threshold
  if (idc > IDC_OPTIMIZATION_THRESHOLD) {
    return {
      status: 'warning',
      color: '#f97316', // Orange
      label: 'Action requise prochainement',
      message: `IDC élevé - Se rapproche du seuil de dépassement significatif`,
      baseThreshold: IDC_BASE_THRESHOLD,
      significantThreshold,
      exceedancePercent,
      requiresAction: true,
      actionType: 'audit',
    }
  }

  // Optimization: Between base and optimization threshold
  if (idc > IDC_BASE_THRESHOLD) {
    return {
      status: 'optimization',
      color: '#eab308', // Yellow
      label: 'Optimisation requise',
      message: `IDC dépasse le seuil réglementaire (${IDC_BASE_THRESHOLD} MJ/m²)`,
      baseThreshold: IDC_BASE_THRESHOLD,
      significantThreshold,
      exceedancePercent,
      requiresAction: true,
      actionType: 'optimization',
    }
  }

  // Compliant: Below base threshold
  return {
    status: 'compliant',
    color: '#10b981', // Green
    label: 'Conforme',
    message: `IDC respecte le seuil réglementaire (${IDC_BASE_THRESHOLD} MJ/m²)`,
    baseThreshold: IDC_BASE_THRESHOLD,
    significantThreshold,
    exceedancePercent,
    requiresAction: false,
    actionType: 'none',
  }
}

/**
 * Estimate annual heating cost from IDC
 *
 * @param idc - Heat consumption index in MJ/m²·year
 * @param area - Energy reference area (SRE) in m²
 * @param heatingType - Type of heating system (optional, for more accurate pricing)
 * @returns Estimated annual cost in CHF
 */
export function estimateAnnualCostFromIDC(
  idc: number,
  area: number,
  heatingType?: string
): number {
  // Convert IDC to total kWh consumption
  const totalMJ = idc * area
  const totalKWh = convertMJtoKWh(totalMJ)

  // Average Swiss energy prices 2025 (CHF/kWh)
  let pricePerKWh = 0.1 // Default (district heating/gas average)

  if (heatingType) {
    const heatingLower = heatingType.toLowerCase()
    if (heatingLower.includes('mazout') || heatingLower.includes('oil')) {
      pricePerKWh = 0.12 // Oil
    } else if (heatingLower.includes('gaz') || heatingLower.includes('gas')) {
      pricePerKWh = 0.11 // Gas
    } else if (heatingLower.includes('pompe') || heatingLower.includes('pac')) {
      pricePerKWh = 0.065 // Heat pump (effective cost with COP 3)
    } else if (
      heatingLower.includes('électr') ||
      heatingLower.includes('electric')
    ) {
      pricePerKWh = 0.2 // Direct electric
    } else if (heatingLower.includes('bois') || heatingLower.includes('wood')) {
      pricePerKWh = 0.08 // Wood pellets
    } else if (
      heatingLower.includes('chaleur') ||
      heatingLower.includes('district')
    ) {
      pricePerKWh = 0.1 // District heating
    }
  }

  return Math.round(totalKWh * pricePerKWh)
}

/**
 * Calculate potential savings if IDC is reduced to target
 *
 * @param currentIDC - Current heat consumption index in MJ/m²·year
 * @param targetIDC - Target heat consumption index in MJ/m²·year
 * @param area - Energy reference area (SRE) in m²
 * @param heatingType - Type of heating system (optional)
 * @returns Savings object with various metrics
 */
export function calculateSavingsPotential(
  currentIDC: number,
  targetIDC: number,
  area: number,
  heatingType?: string
): {
  reductionMJ: number
  reductionPercent: number
  annualSavings: number
  savingsOver20Years: number
  monthlySavings: number
} {
  const reductionMJ = currentIDC - targetIDC
  const reductionPercent = (reductionMJ / currentIDC) * 100

  const currentCost = estimateAnnualCostFromIDC(currentIDC, area, heatingType)
  const targetCost = estimateAnnualCostFromIDC(targetIDC, area, heatingType)
  const annualSavings = currentCost - targetCost

  return {
    reductionMJ,
    reductionPercent: Math.round(reductionPercent * 10) / 10,
    annualSavings: Math.round(annualSavings),
    savingsOver20Years: Math.round(annualSavings * 20),
    monthlySavings: Math.round(annualSavings / 12),
  }
}

/**
 * Get typical IDC range for buildings from a specific period
 * Based on construction standards and typical insulation levels
 *
 * @param buildingYear - Construction year
 * @returns Typical IDC range object
 */
export function getTypicalIDCByPeriod(buildingYear: number): {
  period: string
  typicalMin: number
  typicalMax: number
  average: number
  context: string
} {
  if (buildingYear < 1960) {
    return {
      period: 'Avant 1960',
      typicalMin: 700,
      typicalMax: 1000,
      average: 850,
      context: 'Bâtiments anciens - Aucune isolation',
    }
  }
  if (buildingYear < 1975) {
    return {
      period: '1960-1975',
      typicalMin: 650,
      typicalMax: 900,
      average: 775,
      context: "Pas de normes d'isolation",
    }
  }
  if (buildingYear < 1990) {
    return {
      period: '1975-1990',
      typicalMin: 500,
      typicalMax: 750,
      average: 625,
      context: 'Premières normes énergétiques',
    }
  }
  if (buildingYear < 2000) {
    return {
      period: '1990-2000',
      typicalMin: 400,
      typicalMax: 600,
      average: 500,
      context: 'Normes renforcées',
    }
  }
  if (buildingYear < 2010) {
    return {
      period: '2000-2010',
      typicalMin: 300,
      typicalMax: 500,
      average: 400,
      context: 'Normes modernes',
    }
  }
  return {
    period: '2010+',
    typicalMin: 200,
    typicalMax: 400,
    average: 300,
    context: 'Standards contemporains',
  }
}

/**
 * Estimate renovation cost based on IDC reduction target
 *
 * @param currentIDC - Current heat consumption index
 * @param targetIDC - Target heat consumption index
 * @param area - Building area in m²
 * @returns Cost estimate object
 */
export function estimateRenovationCost(
  currentIDC: number,
  targetIDC: number,
  area: number
): {
  minCost: number
  maxCost: number
  averageCost: number
  scope: string
} {
  const reductionPercent = ((currentIDC - targetIDC) / currentIDC) * 100

  // Light optimization (5-15% reduction)
  if (reductionPercent < 15) {
    return {
      minCost: 5000,
      maxCost: 20000,
      averageCost: 12500,
      scope: 'Optimisation systèmes (réglages, isolation tuyaux)',
    }
  }

  // Moderate renovation (15-30% reduction)
  if (reductionPercent < 30) {
    return {
      minCost: Math.round(area * 150),
      maxCost: Math.round(area * 300),
      averageCost: Math.round(area * 225),
      scope: 'Isolation partielle et optimisation systèmes',
    }
  }

  // Deep renovation (>30% reduction)
  return {
    minCost: Math.round(area * 300),
    maxCost: Math.round(area * 500),
    averageCost: Math.round(area * 400),
    scope: 'Rénovation énergétique complète',
  }
}

/**
 * Get deadline for compliance based on IDC level
 *
 * @param idc - Heat consumption index in MJ/m²·year
 * @param currentYear - Current year (default: now)
 * @returns Deadline information
 */
export function getComplianceDeadline(
  idc: number,
  currentYear: number = new Date().getFullYear()
): {
  hasDeadline: boolean
  year?: number
  yearsRemaining?: number
  urgency: 'none' | 'low' | 'medium' | 'high' | 'critical'
  message: string
} {
  const status = getIDCStatus(idc, currentYear)

  // General deadline for all buildings
  const generalDeadline = 2032
  const yearsToGeneralDeadline = generalDeadline - currentYear

  // Critical: Above significant threshold - 3 years from administrative decision
  if (status.status === 'critical') {
    return {
      hasDeadline: true,
      year: currentYear + 3,
      yearsRemaining: 3,
      urgency: 'critical',
      message: `Rénovation obligatoire dans les 3 ans suivant la décision administrative. Échéance générale: ${generalDeadline}`,
    }
  }

  // Warning or optimization: General deadline 2032
  if (status.status === 'warning' || status.status === 'optimization') {
    return {
      hasDeadline: true,
      year: generalDeadline,
      yearsRemaining: yearsToGeneralDeadline,
      urgency:
        yearsToGeneralDeadline <= 3
          ? 'high'
          : yearsToGeneralDeadline <= 5
            ? 'medium'
            : 'low',
      message: `Échéance générale de conformité: ${generalDeadline} (${yearsToGeneralDeadline} ans restants)`,
    }
  }

  // Compliant: No deadline
  return {
    hasDeadline: false,
    urgency: 'none',
    message: 'Aucune échéance - Bâtiment conforme',
  }
}
